import {
	Drawer,
	DrawerOverlay,
	DrawerContent,
	HStack,
	Button,
	DrawerCloseButton,
	DrawerHeader,
	DrawerBody,
	DrawerFooter,
	VStack,
	Text,
	Stack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import OptionItem from "@components/ChakraUI/OptionItem";
import LeftArrow from "@components/icons/LeftArrow";
import WhiteClose from "@components/icons/WhiteClose";
import ConsultantMiniProfile from "./ConsultantMiniProfile";
import ConsultantProfileItem from "./ConsultantProfileItem";
import type { Consultant } from "@models/Consultant";
import { ConsultantSelectTypeOptions } from "@constants/createOrder";
import type { MenuData } from "@models/Order";
import LoadingIndicator from "@components/Loading";

interface Props {
	isOpen: boolean;
	menuData: MenuData;
	onChangeMenuData: (data: MenuData) => void;
	onClose: () => void;
	consultantsList?: Consultant[];
	isLoading?: boolean;
	/** Overrides the header text, e.g. when inviting to an already published job */
	description?: string;
	/** Consultants already asked/applied, by id with a label ("Önskad"): shown selected and cannot be toggled */
	lockedConsultantLabelById?: Record<string, string>;
	isSubmitting?: boolean;
}

const ConsultantSelectMenu = ({
	isOpen,
	onClose,
	menuData,
	onChangeMenuData,
	consultantsList,
	isLoading = false,
	description = "Välj de konsulter att bjuda in till ditt uppdrag. Dessa kommer få en förfrågan i samband med att upppdraget publiceras där de kan acceptera eller neka.",
	lockedConsultantLabelById = {},
	isSubmitting = false,
}: Props) => {
	const lockedConsultantIds = Object.keys(lockedConsultantLabelById);
	const [selectedConsultantToPreview, setSelectedConsultantToPreview] = useState<Consultant | null>();
	const [tempConsultantSelectType, setTempConsultantSelectType] = useState("");
	const [tempSelectedConsultants, setTempSelectedConsultants] = useState<Consultant[]>([]);

	useEffect(() => {
		setTempConsultantSelectType(menuData.shouldSystemChooseIfSelectedConsultantsNotAvailable ? "YES" : "NO");
		setTempSelectedConsultants(menuData.selectedConsultants);
	}, [menuData]);

	const handleSelectConsultant = (consultant: Consultant) => {
		if (lockedConsultantIds.includes(consultant.id)) return;
		if (tempSelectedConsultants.find((c) => c.id === consultant.id))
			setTempSelectedConsultants((prev) => prev.filter((c) => c.id !== consultant.id));
		else setTempSelectedConsultants((prev) => [...prev, consultant]);
	};
	const newSelectionCount = tempSelectedConsultants.filter((c) => !lockedConsultantIds.includes(c.id)).length;
	const isDisableConsultantSelection = newSelectionCount === 0 || tempConsultantSelectType === "";

	return (
		<Drawer
			placement={"right"}
			size={"sm"}
			onClose={() => {
				onClose();
				setTempConsultantSelectType(ConsultantSelectTypeOptions[0].keyValue);
				setSelectedConsultantToPreview(null);
			}}
			isOpen={isOpen}
		>
			<DrawerOverlay />
			<DrawerContent borderLeftRadius={"25px"}>
				{selectedConsultantToPreview ? (
					<HStack p={3} justify={"space-between"}>
						<Button onClick={() => setSelectedConsultantToPreview(null)} leftIcon={<LeftArrow />} variant="ghost">
							Tillbaka
						</Button>
						<span
							style={{ cursor: "pointer" }}
							onClick={() => {
								onClose();
								setSelectedConsultantToPreview(null);
							}}
						>
							<WhiteClose fillColor="#BFBFBF" />
						</span>
					</HStack>
				) : (
					<DrawerCloseButton />
				)}
				{!selectedConsultantToPreview && (
					<DrawerHeader borderBottomWidth="1px" textAlign={"center"}>
						<Text>Bjud in konsulter</Text>
						<Text fontWeight={400} fontSize={13} color={"#999999"}>
							{description}
						</Text>
					</DrawerHeader>
				)}
				<DrawerBody p={selectedConsultantToPreview ? 6 : 0}>
					{!isLoading && selectedConsultantToPreview && (
						<ConsultantMiniProfile
							id={selectedConsultantToPreview.id}
							previouslyHired={selectedConsultantToPreview.hasCustomerExperience ? true : false}
						/>
					)}
					{!isLoading &&
						!selectedConsultantToPreview &&
						consultantsList &&
						consultantsList
							.sort((consultant) => (consultant.hasCustomerExperience ? -1 : 1))
							.map(({ id, firstName, lastName, profile, hasCustomerExperience }) => (
								<ConsultantProfileItem
									name={`${firstName}`}
									image={profile?.profilePicture}
									description={profile?.presentation}
									previouslyHired={hasCustomerExperience}
									statusLabel={lockedConsultantLabelById[id]}
									id={id}
									key={id}
									isSelected={!!tempSelectedConsultants.find((c) => c.id === id)}
									onSelect={() =>
										handleSelectConsultant({
											id,
											firstName,
											lastName,
											profile,
											hasCustomerExperience,
										})
									}
									onShowProfile={() =>
										setSelectedConsultantToPreview({
											id,
											firstName,
											lastName,
											profile,
											hasCustomerExperience,
										})
									}
								/>
							))}
					{isLoading && <LoadingIndicator size={"s"} />}
				</DrawerBody>
				{!selectedConsultantToPreview && (
					<DrawerFooter borderTopWidth="1px" pt="4">
						<VStack spacing={6}>
							<Stack direction={{ base: "column", md: "row" }} alignItems={"center"} spacing={{ base: 3, md: 1.5 }}>
								<Text fontSize={"13px"} fontWeight={500} m="0">
									Om önskade konsulter ej kan – vill du att vi bokar en annan konsult åt dig?
								</Text>
								<HStack spacing={"5"}>
									{[
										{ label: "Ja", keyValue: "YES" },
										{ label: "Nej", keyValue: "NO" },
									].map((item) => (
										<OptionItem
											key={item.keyValue}
											{...item}
											isSelected={tempConsultantSelectType === item.keyValue}
											onChange={(keyV) => setTempConsultantSelectType(keyV)}
										/>
									))}
								</HStack>
							</Stack>
							<Button
								variant="solid"
								height="40px"
								width={"300px"}
								borderRadius={"20px"}
								color="white"
								bg={"#2A2A2A"}
								mx={"auto"}
								_hover={{
									backgroundColor: isDisableConsultantSelection ? "#9fa0a3" : "#2A2A2A",
								}}
								onClick={() =>
									onChangeMenuData({
										selectedConsultants: tempSelectedConsultants,
										shouldSystemChooseIfSelectedConsultantsNotAvailable: tempConsultantSelectType === "YES",
									})
								}
								isDisabled={isDisableConsultantSelection}
								isLoading={isSubmitting}
							>
								Skicka inbjudan
							</Button>
						</VStack>
					</DrawerFooter>
				)}
			</DrawerContent>
		</Drawer>
	);
};

export default ConsultantSelectMenu;
